import React, { useMemo, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useData from '../hooks/useData';
import useToast from '../hooks/useToast';
import { getPriorityColor, getStatusColor } from '../utils/helpers';
import { ArchiveIcon, RefreshIcon, SunIcon, AddIcon } from '../components/ui/Icons';
import useClickOutside from '../hooks/useClickOutside';
// --- INÍCIO DA ALTERAÇÃO 1: Importando o novo componente reutilizável ---
import PriorityChanger from '../components/ui/PriorityChanger';
// --- FIM DA ALTERAÇÃO 1 ---

const ItemTypes = { TASK: 'task' };

const DraggableTask = ({ task, onTaskClick, projects, onArchive }) => {
    const { handleToggleMyDay } = useData();
    const project = useMemo(() => projects.find(p => p.id === task.projectId), [projects, task.projectId]);
    const priorityColor = getPriorityColor(task.priority);
    const statusColor = getStatusColor(task.status);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.TASK,
        item: { id: task.id },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }), [task]);

    return (
        <div
            ref={drag}
            className={`group relative flex flex-col p-4 rounded-xl shadow-md cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg bg-gray-800 hover:bg-gray-700 mb-3 ${isDragging ? 'opacity-25' : 'opacity-100'}`}
        >
            <div className="absolute left-0 top-0 h-full w-1.5 rounded-l-xl overflow-hidden">
                <div style={{ backgroundColor: priorityColor }} className="h-1/2 w-full"></div>
                <div style={{ backgroundColor: statusColor }} className="h-1/2 w-full"></div>
            </div>

            {/* --- INÍCIO DA ALTERAÇÃO 2: Removida a lógica do PriorityChanger daqui --- */}
            <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <PriorityChanger task={task} />
            {/* --- FIM DA ALTERAÇÃO 2 --- */}
                
                {task.isAlteration && (
                    <div className="text-orange-400" title="Tarefa marcada para alteração">
                        <RefreshIcon size={16} />
                    </div>
                )}
                
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleMyDay(task.id, task.isMyDay);
                    }}
                    title={task.isMyDay ? "Remover de Meu Dia" : "Adicionar ao Meu Dia"}
                    className={`p-1 rounded-full ${task.isMyDay ? 'bg-yellow-500/30 text-yellow-400' : 'bg-white/10 text-gray-400 hover:bg-white/20'} transition-all`}
                >
                    <SunIcon size={16} />
                </button>

                {task.status === 'Concluído' && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onArchive(task.id);
                        }}
                        title="Arquivar Tarefa"
                        className="p-1 bg-indigo-500/50 rounded-full text-white hover:bg-indigo-500 transition-all"
                    >
                        <ArchiveIcon size={16} />
                    </button>
                )}
            </div>

            <div onClick={() => onTaskClick(task)} className="flex-grow pr-16">
                <h4 className="font-bold text-base text-white truncate uppercase mb-1">{task.title}</h4>
                <p className="text-sm text-gray-400 truncate">{project?.name || task.client}</p>
            </div>
        </div>
    );
};

const DropZone = ({ status, tasks, projects, onTaskClick, onDropTask, onArchive }) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: ItemTypes.TASK,
        drop: (item) => onDropTask(item.id, status),
        collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    }), [onDropTask, status]);

    return (
        <div 
            ref={drop} 
            className={`flex-grow min-h-[200px] rounded-lg p-1 transition-colors duration-200 overflow-y-auto ${isOver ? 'bg-white/10' : ''}`}
        >
            {tasks.map(task => (<DraggableTask key={task.id} task={task} projects={projects} onTaskClick={onTaskClick} onArchive={onArchive} />))}
        </div>
    );
};

const FlowBoardComponent = ({ tasks, onTaskClick, onNewTaskClick }) => {
    const { projects, handleUpdateTask } = useData();
    const { showToast } = useToast();
    const [filter, setFilter] = useState('all');
    
    const handleArchiveTask = useCallback((taskId) => {
        handleUpdateTask(taskId, { isArchived: true }, 'arquivou a tarefa.');
        showToast("Tarefa arquivada!", 'info');
    }, [handleUpdateTask, showToast]);

    const handleDropTask = useCallback((taskId, newStatus) => {
        const taskToUpdate = tasks.find(t => t.id === taskId);
        if (taskToUpdate && taskToUpdate.status !== newStatus) {
            let updateData = { status: newStatus };
            let historyAction = `moveu a tarefa para ${newStatus}.`;
            let toastMessage = `Status da tarefa atualizado!`;

            if (newStatus === 'Concluído') {
                updateData.isArchived = true;
                historyAction = `concluiu e arquivou a tarefa.`;
                toastMessage = "Tarefa concluída e arquivada!";
            }
            
            handleUpdateTask(taskId, updateData, historyAction);
            showToast(toastMessage);
        }
    }, [tasks, handleUpdateTask, showToast]);
    
    const tasksToDisplay = useMemo(() => {
        if (filter === 'all') return tasks;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (filter === 'day') { const todayString = today.toISOString().split('T')[0]; return tasks.filter(task => task.deadline === todayString); }
        if (filter === 'week') { const startOfWeek = new Date(today); startOfWeek.setDate(today.getDate() - today.getDay()); const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 6); endOfWeek.setHours(23, 59, 59, 999); return tasks.filter(task => { if (!task.deadline) return false; const taskDate = new Date(new Date(task.deadline).toLocaleString("en-US", {timeZone: "UTC"})); return taskDate >= startOfWeek && taskDate <= endOfWeek; }); }
        return tasks;
    }, [tasks, filter]);

    const zones = [ { title: 'A Fazer', status: 'A Fazer' }, { title: 'Em Progresso', status: 'Em Progresso' }, { title: 'Revisão', status: 'Revisão' }, { title: 'Concluído', status: 'Concluído' }];

    return (
        <div className="p-4 flex flex-col h-full">
            <div className="relative z-10">
                <div className="flex justify-between items-center mb-2"><h2 className="text-3xl font-bold text-white">O Fluxo</h2><div className="flex gap-1 rounded-lg bg-black/20 p-1"><button onClick={() => setFilter('day')} className={`px-3 py-1 text-sm rounded-md transition-colors ${filter === 'day' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}>Dia</button><button onClick={() => setFilter('week')} className={`px-3 py-1 text-sm rounded-md transition-colors ${filter === 'week' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}>Semana</button><button onClick={() => setFilter('all')} className={`px-3 py-1 text-sm rounded-md transition-colors ${filter === 'all' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}>Todas</button></div></div>
                <p className="text-gray-400 mb-6">Arraste as tarefas entre as zonas para mudar o status.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-full">
                {zones.map(zone => {
                    const columnTasks = tasksToDisplay
                        .filter(t => t.status === zone.status)
                        .sort((a, b) => {
                            const priorityOrder = { 'Alta': 1, 'Média': 2, 'Baixa': 3 };
                            return (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
                        });

                    return (
                        <div key={zone.status} className="bg-black/20 rounded-xl flex flex-col p-4 h-full">
                            <h3 className="font-bold text-lg text-white mb-4 flex justify-between items-center flex-shrink-0">
                                <span>{zone.title}</span>
                                <span className="text-sm font-normal text-gray-400 bg-black/30 rounded-full px-2 py-0.5">{columnTasks.length}</span>
                            </h3>
                            
                            <DropZone
                                status={zone.status}
                                tasks={columnTasks}
                                projects={projects}
                                onTaskClick={onTaskClick}
                                onDropTask={handleDropTask}
                                onArchive={handleArchiveTask}
                            />

                            <button
                                onClick={() => onNewTaskClick(zone.status)}
                                className="mt-4 w-full flex items-center justify-center p-2 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-colors flex-shrink-0"
                            >
                                <AddIcon size={16} />
                                <span className="ml-2 text-sm">Adicionar Tarefa</span>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const FlowBoard = (props) => (<DndProvider backend={HTML5Backend}><FlowBoardComponent {...props} /></DndProvider>);

export default FlowBoard;